import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, SearchX } from "lucide-react";
import { products } from "@/data/products";
import { brands, brandName } from "@/data/brands";
import { ProductCard } from "@/components/product/ProductCard";
import { stagger } from "@/components/ui-motion/Reveal";
import { useSeo } from "@/lib/seo";

const sorts = [
  { id: "featured", label: "Featured" },
  { id: "newest", label: "Newest" },
  { id: "price-asc", label: "Price: Low → High" },
  { id: "price-desc", label: "Price: High → Low" },
];
const conditions = ["Excellent", "Good"];
const storages = ["128GB", "256GB"];

const Pill = ({ active, children, ...props }) => (
  <button {...props} className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-white" : "border-border bg-card hover:border-[hsl(var(--ink))]"}`}>
    {children}
  </button>
);

export default function Phones() {
  const [params, setParams] = useSearchParams();
  const brand = params.get("brand") || "";
  const type = params.get("type") || "";
  const condition = params.get("condition") || "";
  const storage = params.get("storage") || "";
  const sort = params.get("sort") || "featured";
  const q = params.get("q") || "";
  const maxPrice = Number(params.get("maxPrice") || 0);

  const set = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: true });
  };

  const heading = type === "new" ? "New Phones" : type === "used" ? "Used Phones" : "All Phones";
  useSeo({
    title: `${brand ? brandName(brand) + " " : ""}${heading} in Rohini — Mobile Specialist`,
    description: `Browse ${heading.toLowerCase()} at Mobile Specialist, Sector 3 Rohini. Search by brand, condition, storage and price. Enquire instantly on WhatsApp.`,
    path: "/phones",
  });

  const filtered = useMemo(() => {
    let list = products.filter((p) => {
      if (brand && p.brand !== brand) return false;
      if (type && p.type !== type) return false;
      if (condition && p.used?.condition !== condition) return false;
      if (storage && p.storage !== storage) return false;
      if (maxPrice && p.price > maxPrice) return false;
      if (q) {
        const hay = `${p.brandName} ${p.model} ${p.color} ${p.storage}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
    list = [...list];
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "newest") list.sort((a, b) => Number(b.id) - Number(a.id));
    else list.sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured));
    return list;
  }, [brand, type, condition, storage, sort, q, maxPrice]);

  return (
    <div className="container-wide py-10" data-testid="phones-page">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--ink-soft))]">Catalogue</p>
        <h1 className="mt-2 font-display text-4xl font-bold sm:text-5xl">{heading}</h1>
        <p className="mt-2 text-[hsl(var(--ink-soft))]">{filtered.length} {filtered.length === 1 ? "phone" : "phones"} available{q ? ` for “${q}”` : ""}. <span className="text-xs">Demo listings.</span></p>
      </header>

      <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
        <aside className="space-y-6" data-testid="filters">
          <div className="flex items-center gap-2 text-sm font-semibold"><SlidersHorizontal className="h-4 w-4" /> Filters</div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ink-soft))]">Type</p>
            <div className="flex flex-wrap gap-2">
              <Pill active={!type} onClick={() => set("type", "")}>All</Pill>
              <Pill active={type === "new"} onClick={() => set("type", "new")}>New</Pill>
              <Pill active={type === "used"} onClick={() => set("type", "used")}>Used</Pill>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ink-soft))]">Brand</p>
            <div className="flex flex-wrap gap-2">
              <Pill active={!brand} onClick={() => set("brand", "")}>All</Pill>
              {brands.map((b) => <Pill key={b.id} active={brand === b.id} onClick={() => set("brand", b.id)}>{b.name}</Pill>)}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ink-soft))]">Condition (Used)</p>
            <div className="flex flex-wrap gap-2">
              <Pill active={!condition} onClick={() => set("condition", "")}>Any</Pill>
              {conditions.map((c) => <Pill key={c} active={condition === c} onClick={() => set("condition", c)}>{c}</Pill>)}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ink-soft))]">Storage</p>
            <div className="flex flex-wrap gap-2">
              <Pill active={!storage} onClick={() => set("storage", "")}>Any</Pill>
              {storages.map((s) => <Pill key={s} active={storage === s} onClick={() => set("storage", s)}>{s}</Pill>)}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ink-soft))]">Max Price: {maxPrice ? `₹${maxPrice.toLocaleString("en-IN")}` : "Any"}</p>
            <input type="range" min="30000" max="140000" step="5000" value={maxPrice || 140000}
              onChange={(e) => set("maxPrice", e.target.value === "140000" ? "" : e.target.value)}
              className="w-full accent-[hsl(var(--accent))]" data-testid="price-filter" aria-label="Maximum price" />
          </div>
        </aside>

        <div>
          <div className="mb-5 flex items-center justify-between">
            <label className="text-sm text-[hsl(var(--ink-soft))]" htmlFor="sort">Sort by</label>
            <select id="sort" value={sort} onChange={(e) => set("sort", e.target.value)} data-testid="sort-select"
              className="rounded-full border border-border bg-card px-4 py-2 text-sm outline-none">
              {sorts.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          {filtered.length ? (
            <motion.div variants={stagger} initial="hidden" animate="show" className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
            </motion.div>
          ) : (
            <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-20 text-center" data-testid="empty-state">
              <SearchX className="h-10 w-10 text-[hsl(var(--ink-soft))]" />
              <p className="mt-4 font-semibold">No phones match your filters</p>
              <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">Try clearing a filter, or message us on WhatsApp for current stock.</p>
              <button onClick={() => setParams({}, { replace: true })} className="btn-outline mt-6">Clear all filters</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
